import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Heart, Play } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ProductCard = ({ product, onAddToCart, onAddToWishlist, onPlayAudio, isInWishlist = false }) => {
    const { isAuthenticated } = useAuth();

    const handleAddToCart = (e) => {
        e.preventDefault();
        if (onAddToCart) onAddToCart(product.id);
    };

    const handleAddToWishlist = (e) => {
        e.preventDefault();
        if (onAddToWishlist) onAddToWishlist(product.id);
    };

    const handlePlay = (e) => {
        e.preventDefault();
        if (onPlayAudio) onPlayAudio(product);
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'sample_pack':
                return 'SAMPLE PACK';
            case 'song':
                return 'SONG';
            case 'album':
                return 'ALBUM';
            default:
                return type.toUpperCase();
        }
    };

    return (
        <Link 
            to={`/product/${product.id}`} 
            className="product-card block group"
            data-testid={`product-card-${product.id}`}
        >
            <div className="relative aspect-square overflow-hidden">
                <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                        <button 
                            onClick={handlePlay}
                            className="p-3 bg-primary rounded-full hover:bg-primary/90 transition-colors"
                            data-testid={`play-button-${product.id}`}
                        >
                            <Play className="w-5 h-5" />
                        </button>
                        {isAuthenticated && (
                            <div className="flex gap-2">
                                <button 
                                    onClick={handleAddToWishlist}
                                    className={`p-3 rounded-full transition-colors ${isInWishlist ? 'bg-primary text-white' : 'bg-white/10 hover:bg-white/20'}`}
                                    data-testid={`wishlist-button-${product.id}`}
                                >
                                    <Heart className="w-5 h-5" fill={isInWishlist ? 'currentColor' : 'none'} />
                                </button>
                                <button 
                                    onClick={handleAddToCart}
                                    className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
                                    data-testid={`add-to-cart-button-${product.id}`}
                                >
                                    <ShoppingCart className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-black/80 text-xs font-mono uppercase tracking-wider" data-testid={`product-type-${product.id}`}>
                        {getTypeLabel(product.type)}
                    </span>
                </div>
            </div>

            <div className="p-4">
                <h3 className="font-semibold text-lg mb-1 tracking-tight" data-testid={`product-name-${product.id}`}>
                    {product.name}
                </h3>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{product.description}</p>
                <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-primary" data-testid={`product-price-${product.id}`}>
                        ${product.price}
                    </span>
                    {isAuthenticated && (
                        <button 
                            onClick={handleAddToCart}
                            className="btn-secondary text-xs px-4 py-2"
                        >
                            Add to Cart
                        </button>
                    )}
                </div>
            </div>
        </Link>
    );
};

export default ProductCard;
